import React, { useState } from 'react';
import { vibrateTap } from '@/utils/sound';

/**
 * CTAButton — the single primary-action primitive.
 *
 * Design intent: one button, many states — idle breath, pressed scale,
 * loading spinner, disabled hush. Every variant has an opinionated
 * shadow so the button reads as elevated, not flat.
 *
 * Props:
 *   label       string — required
 *   onClick     fn
 *   disabled    bool
 *   loading     bool  — swaps label for a spinner, auto-disables
 *   variant     'primary' | 'secondary' | 'ghost'
 *   breath      bool  — idle pulse on primary variant (default true)
 *   arrow       bool  — show trailing "→"          (default true)
 *   icon        ReactNode — leading icon
 *   type        'button' | 'submit'  (default 'button')
 *   ariaLabel   string
 */
export default function CTAButton({
  label,
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  breath = true,
  arrow = true,
  icon,
  type = 'button',
  ariaLabel,
}) {
  const [pressed, setPressed] = useState(false);
  const isDisabled = disabled || loading;

  const base = {
    position: 'relative',
    width: '100%',
    minHeight: 52,
    padding: '14px 22px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    border: 'none',
    borderRadius: 999,
    fontFamily: 'var(--font-ui)',
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition:
      'opacity 180ms ease, box-shadow 220ms ease, background 220ms ease, color 220ms ease, transform 120ms cubic-bezier(0.2,0.8,0.2,1)',
    transform: pressed && !isDisabled ? 'scale(0.97)' : 'scale(1)',
    WebkitTapHighlightColor: 'transparent',
    overflow: 'hidden',
  };

  const variants = {
    primary: {
      ...base,
      background: 'var(--primary)',
      color: '#ffffff',
      boxShadow:
        '0 10px 24px -10px rgba(168, 0, 63, 0.38), inset 0 1px 0 rgba(255,255,255,0.22)',
    },
    secondary: {
      ...base,
      background: 'transparent',
      color: 'var(--primary)',
      border: '1.5px solid var(--primary)',
      boxShadow: 'none',
    },
    ghost: {
      ...base,
      background: 'var(--surface)',
      color: 'var(--primary)',
      border: '1px solid var(--border)',
      boxShadow: '0 2px 10px rgba(45, 26, 31, 0.05)',
    },
  };

  const style = variants[variant] || variants.primary;
  // Idle breath — only on the primary variant, only when enabled, and
  // only when the caller hasn't opted out. Secondary / ghost stay
  // static so the primary action draws the eye.
  const breathe = breath && !isDisabled && variant === 'primary';

  function handleClick(e) {
    if (isDisabled) return;
    try { vibrateTap(); } catch { /* noop */ }
    onClick?.(e);
  }

  return (
    <button
      type={type}
      className={breathe ? 'motion-breath' : undefined}
      style={style}
      onClick={handleClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      onPointerDown={() => !isDisabled && setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
    >
      {loading ? (
        <Spinner
          color={
            variant === 'primary' ? '#ffffff' : 'var(--primary)'
          }
        />
      ) : (
        <>
          {icon && (
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>{icon}</span>
          )}
          <span>{label}</span>
          {arrow && (
            <span
              aria-hidden="true"
              style={{
                fontSize: 15,
                transform: pressed && !isDisabled ? 'translateX(2px)' : 'translateX(0)',
                transition: 'transform 140ms ease',
              }}
            >
              {'→'}
            </span>
          )}
        </>
      )}
    </button>
  );
}

function Spinner({ color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 20,
        height: 20,
        display: 'inline-block',
        borderRadius: '50%',
        border: `2px solid ${color}`,
        borderTopColor: 'transparent',
        animation: 'bbSpin 820ms linear infinite',
      }}
    />
  );
}

// Scoped spin keyframe — same pattern as Skeleton.jsx: injected once
// at import time, idempotent.
if (typeof document !== 'undefined' && !document.getElementById('bb-cta-anim')) {
  const sheet = document.createElement('style');
  sheet.id = 'bb-cta-anim';
  sheet.textContent = `
@keyframes bbSpin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  @keyframes bbSpin { to { transform: none; } }
}`;
  document.head.appendChild(sheet);
}
